import { assert } from '@sama/js-core';
import { BoxGeometry, EdgesGeometry } from 'three';
import {
  Point3D,
  VisibilityState,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { CuboidMaterial } from '../../../materials/cuboid/cuboid-material';
import { CuboidLocationState3D } from '../../../state/cuboid-location-state-3d';
import { PointCloudContentState } from '../../../state/point-cloud-content-state';
import { interpolateLocation } from '../../../utils/point-cloud-interpolation-helper';
import { distanceBetween, subtractPoints } from '../../../utils/point-utils';
import { shouldCurveEdges } from '../../../utils/projections/projection-utils';
import { computeCuboidOffset } from '../../../utils/timestamp-compensation-utils';
import { MaterialService } from '../../material.service';
import { getSideViewRendererLayers } from '../../render-utils';
import {
  AbstractMeshHelper,
  ConfigureMeshesConfiguration,
  RenderConfiguration,
} from '../abstract-mesh-helper';
import { getShapeColor, getShapeColorIfHighlighted } from '../color-utils';
import { CuboidAxesMeshHelper } from './cuboid-axes-mesh';
import { CuboidBoxMesh } from './cuboid-box-mesh';
import { CuboidFoiSelector } from './cuboid-foi-selector';
import { getSensorPosition } from './cuboid-utils';
import { CuboidVertices } from './cuboid-vertices';
import { CuboidWireframeMesh } from './cuboid-wireframe-mesh';
import { FaceOfInterestMesh } from './face-of-interest-mesh';

// Face index order should follow ThreeJS face geometry order
export enum CuboidFace {
  Left = 0,
  Right,
  Back,
  Front,
  Top,
  Bottom,
}

export class CuboidHelper extends AbstractMeshHelper {
  index: number;

  box = new CuboidBoxMesh();
  private wireframeMesh = new CuboidWireframeMesh(this.box.geometry);
  private faceOfInterestMesh = new FaceOfInterestMesh();
  private vertices = new CuboidVertices();
  private axes: CuboidAxesMeshHelper;
  private foiSelectors: Map<CuboidFace, CuboidFoiSelector> = new Map();

  constructor(
    private parameters: {
      index: number;
      isTraining: boolean;
      overrideColor?: string;
    },
  ) {
    super();
    this.index = parameters.index;

    this.axes = new CuboidAxesMeshHelper(this.index);
    this.box = new CuboidBoxMesh();
    this.box.setUserData(this.index, parameters.isTraining);
    this.vertices.setUserData(this.index);

    this.box.add(this.wireframeMesh);
    this.box.add(this.faceOfInterestMesh);
    const facesWithSelector = [
      CuboidFace.Front,
      CuboidFace.Right,
      CuboidFace.Back,
      CuboidFace.Left,
    ];
    facesWithSelector.forEach((face) => {
      const selector = new CuboidFoiSelector(face);
      this.foiSelectors.set(face, selector);
      this.box.add(selector);
    });
  }

  override prepareToRender(
    config: RenderConfiguration,
    state: PointCloudContentState,
    materialService: MaterialService,
  ) {
    super.prepareToRender(config, state, materialService);
    this.axes.prepareToRender(config, state, materialService);
  }

  dispose() {
    this.box.dispose();
    this.wireframeMesh.dispose();
    this.faceOfInterestMesh.dispose();
    this.vertices.dispose();
  }

  getMeshes() {
    return [this.box, this.vertices, ...this.axes.getMeshes()];
  }

  getBoxWithoutSegments() {
    const box = this.box.clone();
    box.geometry = new BoxGeometry();
    return box;
  }

  previewFaceOfInterest(face: CuboidFace) {
    this.faceOfInterestMesh.placeFaceOfInterest(this.box.scale, face);
  }

  resetFaceOfInterest() {
    this.faceOfInterestMesh.placeFaceOfInterest(this.box.scale);
  }

  updateLocation(
    location: CuboidLocationState3D,
    state: PointCloudContentState,
  ) {
    this.box.updateLocation(location);
    if (shouldCurveEdges(state)) {
      const sensorPosition = getSensorPosition(state);
      // We add more segments to the geometry to make it curved when distorting
      this.updateGeometrySegments(
        location,
        distanceBetween(sensorPosition, location.position),
      );
    }

    this.resetFaceOfInterest();
  }

  protected override updateRendererLayers(
    state: PointCloudContentState,
    renderConfig: RenderConfiguration,
  ) {
    const cuboidState = this.getCuboidState3D(state);

    this.box.updateRendererLayers(state, renderConfig, cuboidState);
    this.wireframeMesh.updateRendererLayers(state, renderConfig, cuboidState);
    this.vertices.updateRendererLayers(state, renderConfig, cuboidState);
    this.faceOfInterestMesh.updateRendererLayers(
      state,
      renderConfig,
      cuboidState,
    );
    this.foiSelectors.forEach((selector) =>
      selector.updateRendererLayers(state, renderConfig, this.index),
    );
  }

  protected override configureMeshes(config: ConfigureMeshesConfiguration) {
    const { state } = config;
    const cuboidState = this.getCuboidState3D(state);

    const { frameNumber } = state.render;
    const location = interpolateLocation(
      cuboidState.key_locations,
      frameNumber,
    );

    this.updateStyle(location.visibility, config);
    this.updateLocation(location, state);
    this.vertices.updatePosition(location);
    this.vertices.updateScale(config);
    this.foiSelectors.forEach((selector) => selector.update(config, location));
  }

  private updateStyle(
    visibility: VisibilityState,
    config: ConfigureMeshesConfiguration,
  ) {
    const { state, outputName, isUsingNewEditPanels, cameraLayer } = config;
    const shapeColor = this.getShapeColor(state);
    const highlightedColor = this.getHighlightColor(state);

    const boxColor =
      isUsingNewEditPanels && getSideViewRendererLayers().includes(cameraLayer)
        ? highlightedColor
        : shapeColor;

    this.box.updateMaterial(config, visibility, boxColor);
    this.wireframeMesh.updateMaterial(config, highlightedColor);
    this.faceOfInterestMesh.updateMaterial(config, highlightedColor);

    this.updateMaterialOffset(state, outputName, [
      this.box.material,
      this.wireframeMesh.material,
      this.faceOfInterestMesh.material,
    ]);
  }

  private updateMaterialOffset(
    state: PointCloudContentState,
    outputName: string,
    materials: CuboidMaterial[],
  ) {
    if (state.output.meta.enable_motion_compensation) {
      const offset = computeCuboidOffset(state, outputName, this.index);
      for (const material of materials) {
        material.setUniform('offset', offset);
        material.uniformsNeedUpdate = true;
      }
    }
  }

  private getShapeColor(state: PointCloudContentState) {
    return this.parameters.overrideColor
      ? this.parameters.overrideColor
      : getShapeColor(this.getCuboidState3D(state), state);
  }

  private getHighlightColor(state: PointCloudContentState) {
    return this.parameters.overrideColor
      ? this.parameters.overrideColor
      : getShapeColorIfHighlighted(this.getCuboidState3D(state), state);
  }

  private updateGeometrySegments(
    { scale }: CuboidLocationState3D,
    distanceFromSensor: number,
  ) {
    const distanceBetweenSegments = 0.4;
    // For distances greater than 10 units, decrease the amount of segments
    const distanceFactor = Math.max(distanceFromSensor - 10, 1);

    const numberOfSegments = scale.map((s) =>
      Math.floor(Math.max(s / distanceBetweenSegments / distanceFactor, 1)),
    ) as Point3D;

    if (shouldUpdateSegments(numberOfSegments, this.box.geometry)) {
      this.box.geometry.dispose();
      this.box.geometry = new BoxGeometry(
        1,
        1,
        1,
        numberOfSegments[0],
        numberOfSegments[1],
        numberOfSegments[2],
      );
      this.wireframeMesh.geometry.dispose();
      this.wireframeMesh.geometry = new EdgesGeometry(this.box.geometry);
    }
  }

  private getCuboidState3D(state: PointCloudContentState) {
    const cuboids = this.parameters.isTraining
      ? state.render.trainingShapes.cuboids
      : state.render.cuboids;
    const cuboid = cuboids[this.index];
    assert(cuboid, 'cuboid should exist');
    return cuboid;
  }
}

function shouldUpdateSegments(segments: Point3D, geometry: BoxGeometry) {
  const currentNumberOfSegments: Point3D = [
    geometry.parameters.widthSegments,
    geometry.parameters.heightSegments,
    geometry.parameters.depthSegments,
  ];

  return subtractPoints(segments, currentNumberOfSegments).some((s) => s !== 0);
}
